<template>
  <!-- 全局地磅条: 常驻顶部 header, 切页不断开串口连接 -->
  <!-- [2026-08-14] 本机无外设硬件模式: 整条隐藏 (组件仍常驻, 连接已全部断开, watch 恢复) -->
  <div v-if="!hardwareDisabled" class="global-scale-bar">
    <!-- [2026-10-11] 外设服务离线红徽标: 探活轮询写入 store.serviceOnline. 离线时双磅 connected
         已被斩断(下方读数变 --), 这里给一个常驻、可点击的稳态提示, 替代原来 30s 一弹的排查弹窗 -->
    <span
      v-if="serviceOnline === false"
      class="gsb-svc-offline"
      title="本机外设服务(127.0.0.1:8765)不可达：读卡/地磅都不可用。点击查看排查指引（区分服务未启动/浏览器拦截）"
      @click="openServiceHelper"
    >
      <span class="gsb-dot off" />
      外设服务不可达
    </span>
    <div
      v-for="s in scaleSlots"
      :key="s.scaleKey"
      class="gsb-item"
      :class="{ connected: s.connected }"
    >
      <span class="gsb-dot" :class="{ on: s.connected }" />
      <span class="gsb-name">{{ s.name }}</span>
      <span class="gsb-weight">{{ s.connected ? formatN(s.currentWeight) : '--' }}</span>
      <span class="gsb-unit">KG</span>
      <!-- [2026-09-04] 普通连接 → 检测连接: 先按上次成功参数直连, 失败自动按检测步骤排查到成功, 失败给根因 -->
      <a-button
        v-if="!s.connected && !s.connecting && !s.probing"
        type="primary"
        size="small"
        class="gsb-btn"
        @click="connectScaleWithProbe(s.scaleKey)"
      >检测连接</a-button>
      <a-button
        v-else-if="s.probing"
        size="small"
        class="gsb-btn"
        disabled
      >检测中</a-button>
      <a-button
        v-else-if="s.connecting"
        size="small"
        class="gsb-btn"
        disabled
      >连接中</a-button>
      <a-button
        v-else
        size="small"
        class="gsb-btn"
        @click="disconnectScale(s.scaleKey)"
      >断开</a-button>
    </div>
    <!-- [2026-07-18] 顶部"填入毛重/皮重"切换按钮已停用.
         原因: 单次过磅改造后, 毛重/皮重各自有"使用此读数"按钮 (target 由按钮位置隐式决定),
         WeighTab 双磅卡按钮不再读 store.useScaleTarget, 此按钮点击无实际效果, 保留会误导用户.
         代码保留 (computed/mutation 不删, 向后兼容), 仅模板隐藏. 如需恢复改 v-if="true". -->
    <!--
    <a-button
      size="small"
      class="gsb-btn gsb-target-btn"
      :type="useScaleTarget === 'tare' ? 'primary' : 'default'"
      @click="toggleUseScaleTarget"
    >填入{{ useScaleTarget === 'tare' ? '皮重' : '毛重' }}</a-button>
    -->
  </div>
</template>

<script>
/**
 * 全局地磅条 — 常驻 MainLayout header, 全局唯一持有串口连接.
 *
 * 为什么放全局: 原来地磅连接挂在 EntryV1/Exit 上, 切页时 deactivated→断开 + activated→重连,
 * 频繁卸载加载既慢又不稳定(端口残留/抢占用). 提到全局后连接只建立一次, 切页纹丝不动.
 *
 * 本组件 mix weighbridgeMixin 持有串口连接管理(connectScale/disconnectScale/tryAutoConnect...),
 * 读数通过 Vuex store.deviceStatus 全局共享, EntryV1/Exit 只读 store 不再管连接.
 */
import weighbridgeMixin from '@/mixins/weighbridgeMixin'
import { scaleName, scaleSerialMap, scaleBaudMap, scalePortMap, scaleProtocolMap, scaleComPortMap, scaleEnabledMap, loadHardwareDisabled, getScaleConnectMode, WEIGH_CONFIG_SYNCED_EVENT } from '@/utils/weighDevice'
import { openPeripheralAccessHelper } from '@/utils/peripheralHealth'
import { ScaleStream } from '@/utils/scaleClient'
import { formatN } from '@/views/grain/shared/formatUtil'

export default {
  name: 'GlobalScaleBar',
  mixins: [weighbridgeMixin],
  created() {
    // [2026-07-31] 从 localStorage 水合磅启用状态到 store (供顶栏/过磅卡片/弹窗响应式显隐).
    //   本组件常驻 MainLayout header, 是全局唯一持有串口连接的地方, 由它负责水合最合适.
    this.$store.commit('SET_SCALE_ENABLED', scaleEnabledMap())
    // [2026-08-14] 水合"本机无外设硬件"总开关 (本机全局, 不按工厂隔离; weighbridgeMixin mounted 会读它).
    this.$store.commit('SET_HARDWARE_DISABLED', loadHardwareDisabled())
  },
  computed: {
    entryScale() { return this.$store.state.deviceStatus.entryScale },
    exitScale() { return this.$store.state.deviceStatus.exitScale },
    // [单次过磅] "使用此读数"填入毛重还是皮重 (全局共享, 入库/出库单次模式读此值)
    useScaleTarget() { return this.$store.state.deviceStatus.useScaleTarget },
    // weighbridgeMixin 双轨读取这 5 个 map（工厂级配置, 从 localStorage 读）
    // web 模式用 weighbridgeAutoConnectMap/weighbridgePortMap；jinglun 模式用 weighbridgeComPortMap；波特率/协议两者共用
    weighbridgeAutoConnectMap() { return scaleSerialMap() },
    weighbridgeBaudRateMap() { return scaleBaudMap() },
    weighbridgePortMap() { return scalePortMap() },
    weighbridgeProtocolMap() { return scaleProtocolMap() },
    weighbridgeComPortMap() { return scaleComPortMap() },
    // [2026-07-31] 磅启用状态 (响应式, 供 scaleSlots 过滤 + watch 断开禁用磅)
    scaleEnabled() { return this.$store.state.deviceStatus.scaleEnabled },
    // [2026-08-14] 本机无外设硬件总开关 (配置页保存即生效, 不需刷新页面)
    hardwareDisabled() { return this.$store.state.deviceStatus.hardwareDisabled },
    // [2026-10-11] 外设服务在线状态 (MainLayout 探活轮询写入; null=未知)
    serviceOnline() { return this.$store.state.deviceStatus.serviceOnline },
    // [2026-07-31] 当前工厂 ID (磅配置按工厂隔离, 切厂需重新水合启用状态)
    factoryId() {
      const s = this.$store.state
      return (s.externalOrg && s.externalOrg.saleOrgId) || (s.loginUser && s.loginUser.externalOrgId) || ''
    },
    // 双磅展示数据 (供 v-for 渲染) — [2026-07-31] 只保留启用的磅
    scaleSlots() {
      const en = this.scaleEnabled
      return [
        {
          scaleKey: 'entryScale',
          name: scaleName('entry'),
          connected: !!this.entryScale.connected,
          connecting: !!this.scaleConnecting.entryScale,
          probing: !!this.scaleProbing.entryScale,
          currentWeight: this.entryScale.currentWeight || 0
        },
        {
          scaleKey: 'exitScale',
          name: scaleName('exit'),
          connected: !!this.exitScale.connected,
          connecting: !!this.scaleConnecting.exitScale,
          probing: !!this.scaleProbing.exitScale,
          currentWeight: this.exitScale.currentWeight || 0
        }
      ].filter(s => s.scaleKey === 'entryScale' ? en.entry : en.exit)
    }
  },
  watch: {
    // [2026-07-31] 磅被禁用时, 主动断开其串口连接 (大磅的一切都不再触发)
    scaleEnabled: {
      deep: true,
      handler(en) {
        if (!en.entry && this.entryScale.connected) this.disconnectScale('entryScale')
        if (!en.exit && this.exitScale.connected) this.disconnectScale('exitScale')
      }
    },
    // [2026-08-14] 本机无外设硬件开关联动 (配置页保存即生效):
    //   开 → 断开全部地磅连接 + 退订 WS + 取消自动连接; 关 → 恢复运行时 (订阅 + 自动连接).
    // [2026-08-17] 开(无硬件)时明确关闭服务端串口 (closeServicePort=true → 服务端 desired=false,
    //   否则服务端调和循环会立刻重连回来跟开关打架).
    hardwareDisabled(disabled) {
      if (disabled) this._stopScaleRuntime(true)
      else this._startScaleRuntime()
    },
    // [2026-07-31] 切换烘干场时, 磅启用配置按工厂隔离, 重新从 localStorage 水合
    factoryId(v) {
      if (v) this.$store.commit('SET_SCALE_ENABLED', scaleEnabledMap())
    },
    // [2026-10-11] 外设服务复活(离线→在线): 加速恢复。离线边沿的斩断已在
    // peripheralHealth 探活器里做过 (SET_SCALE_CONNECTED false), 这里只管恢复——
    // WS 断线重连是指数退避(最长 15s), 服务刚复活时主动 forceReconnect 拉直,
    // 再对账一次服务端真实状态(服务端自启时调和循环会自动重连地磅, 前端只需对齐)。
    serviceOnline(online) {
      if (online !== true) return
      if (this._isHardwareDisabled() || getScaleConnectMode() !== 'jinglun') return
      this._subscribeScaleStream() // 幂等 (mixin 内有 _scaleUnsub 守卫)
      ScaleStream.forceReconnect() // 退避最长 15s, 复活瞬间拉直
      this._reconcileScaleStatus()
    }
  },
  mounted() {
    // [2026-08-17] 好粮宝本地服务配置同步完成事件: localStorage 可能被服务端新值覆盖
    // (清缓存后恢复/另一浏览器改过配置) → 重新水合 + 状态对账 + 幂等重连。
    this._onWeighConfigSynced = (e) => {
      this.$store.commit('SET_SCALE_ENABLED', scaleEnabledMap())
      this.$store.commit('SET_HARDWARE_DISABLED', loadHardwareDisabled())
      if (!this._isHardwareDisabled()) {
        if (getScaleConnectMode() === 'jinglun') {
          this._subscribeScaleStream()
          this._reconcileScaleStatus()
          this._autoConnectRetryCount = 0 // 允许重新走自动连接重试
          this.tryAutoConnectAuthorizedScales()
        }
      }
    }
    window.addEventListener(WEIGH_CONFIG_SYNCED_EVENT, this._onWeighConfigSynced)
  },
  beforeDestroy() {
    if (this._onWeighConfigSynced) {
      window.removeEventListener(WEIGH_CONFIG_SYNCED_EVENT, this._onWeighConfigSynced)
    }
  },
  methods: {
    formatN,
    // [2026-10-11] 离线徽标点击 → 手动打开排查弹窗 (force=true 不受节流/离线抑制)
    openServiceHelper() {
      openPeripheralAccessHelper(true)
    },
    // [单次过磅] 切换"使用此读数"填入目标: 毛重 ↔ 皮重
    toggleUseScaleTarget() {
      const next = this.useScaleTarget === 'gross' ? 'tare' : 'gross'
      this.$store.commit('SET_USE_SCALE_TARGET', next)
    }
  }
}
</script>

<style lang="less" scoped>
.global-scale-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 6px;
}

.gsb-item {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 4px;
  background: #fafafa;
  border: 1px solid #e8e8e8;
  font-size: 12px;
  line-height: 1;
  white-space: nowrap;

  &.connected {
    background: #f6ffed;
    border-color: #b7eb8f;
  }
}

.gsb-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #d9d9d9;
  flex-shrink: 0;

  &.on {
    background: #52c41a;
    box-shadow: 0 0 4px rgba(82, 196, 26, 0.5);
  }

  // [2026-10-11] 外设服务离线红点 (离线徽标内)
  &.off {
    background: #f5222d;
    box-shadow: 0 0 4px rgba(245, 34, 45, 0.5);
  }
}

// [2026-10-11] 外设服务离线徽标: 常驻稳态提示, 点击打开排查弹窗
.gsb-svc-offline {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 4px;
  background: #fff1f0;
  border: 1px solid #ffa39e;
  color: #cf1322;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
}

.gsb-name {
  color: #595959;
  font-weight: 500;
}

.gsb-weight {
  font-family: 'Courier New', monospace;
  font-size: 15px;
  font-weight: 700;
  color: #1890ff;
  min-width: 32px;
  text-align: right;
}

.gsb-unit {
  color: #8c8c8c;
  font-size: 11px;
}

.gsb-btn {
  margin-left: 2px;
  padding: 0 8px;
  height: 22px;
  font-size: 12px;
}
</style>
